import type { PlaybackMode } from '../database/custom-types/playback-mode'
import type {
  IChannelPayloadPlaylistAssignment,
  IPlayerRegistrationPlaylistEntry,
  IScheduleRootAssignment
} from '../models/player-registration-payload'
import type { TFrame } from '../models/playlist'

export type { IPlayerRegistrationPlaylistEntry } from '../models/player-registration-payload'

const REGISTRATION_ROW_META_KEYS = [
  'version',
  'playbackMode',
  'scheduleRootUrn',
  'scheduleRootStartDate',
  'scheduleRootEndDate',
  'embeddedPlaylistFrames',
  'widgetAssetMetadata',
  'hlsVideoAssetMetadata'
] as const

function assignmentFromRegistrationEntry(
  row: IPlayerRegistrationPlaylistEntry
): IChannelPayloadPlaylistAssignment {
  const {
    version: _v,
    playbackMode: _pm,
    scheduleRootUrn: _sr,
    scheduleRootStartDate: _srs,
    scheduleRootEndDate: _sre,
    embeddedPlaylistFrames: _emb,
    widgetAssetMetadata: _wam,
    hlsVideoAssetMetadata: _hls,
    ...assignment
  } = row
  return assignment
}

/**
 * Flattens nested registration `schedule[]` roots into one wire row per playlist slot.
 * Each row carries {@link IPlayerRegistrationPlaylistEntry.scheduleRootUrn} so the player can
 * regroup multi-child masters (scheduling embeddings) without losing root identity.
 *
 * Channel flight dates go on `scheduleRootStartDate` / `scheduleRootEndDate`; playlist/embedding
 * windows stay on `startDate` / `endDate` so a dated list flight does not erase tighter child windows.
 */
export function flattenScheduleRootsToRegistrationPlaylists(
  roots: readonly IScheduleRootAssignment[]
): IPlayerRegistrationPlaylistEntry[] {
  const out: IPlayerRegistrationPlaylistEntry[] = []
  for (const root of roots) {
    for (const pl of root.playlists ?? []) {
      out.push({
        ...pl,
        scheduleRootUrn: root.scheduleRootUrn,
        scheduleRootStartDate: root.startDate ?? null,
        scheduleRootEndDate: root.endDate ?? null,
        version: root.version ?? 0,
        playbackMode: root.playbackMode,
        startDate: pl.startDate ?? null,
        endDate: pl.endDate ?? null,
        widgetConfig: root.widgetConfig ?? pl.widgetConfig ?? null,
        ...(root.embeddedPlaylistFrames != null && Object.keys(root.embeddedPlaylistFrames).length > 0
          ? { embeddedPlaylistFrames: root.embeddedPlaylistFrames }
          : {}),
        ...(root.widgetAssetMetadata != null ? { widgetAssetMetadata: root.widgetAssetMetadata } : {}),
        ...(root.hlsVideoAssetMetadata != null ? { hlsVideoAssetMetadata: root.hlsVideoAssetMetadata } : {})
      })
    }
  }
  return out
}

/**
 * Rebuilds nested schedule roots from flat registration `playlists[]` (player playback helpers).
 * Groups by {@link IPlayerRegistrationPlaylistEntry.scheduleRootUrn} when present; falls back to
 * `playlistUrn` for older payloads that omitted the field (leaf self-root rows).
 *
 * Root channel dates come from `scheduleRootStartDate` / `scheduleRootEndDate` when present.
 * Legacy rows (no scheduleRoot* fields) use the row’s `startDate` / `endDate` as the channel window
 * once (first row), matching pre-`scheduleRootUrn` leaf behavior.
 */
export function groupRegistrationPlaylistsToScheduleRoots(
  entries: readonly IPlayerRegistrationPlaylistEntry[]
): IScheduleRootAssignment[] {
  const order: string[] = []
  const byRoot = new Map<string, IScheduleRootAssignment>()

  for (const row of entries) {
    const rootUrn = (row.scheduleRootUrn ?? row.playlistUrn).trim()
    if (rootUrn === '') {
      continue
    }
    const key = rootUrn.toLowerCase()
    let root = byRoot.get(key)
    if (root == null) {
      order.push(key)
      const hasExplicitRootDates = 'scheduleRootStartDate' in row || 'scheduleRootEndDate' in row
      root = {
        scheduleRootUrn: rootUrn,
        title: row.title,
        version: row.version ?? 0,
        description: null,
        playbackMode: row.playbackMode as PlaybackMode,
        customSequence: null,
        startDate: hasExplicitRootDates
          ? (row.scheduleRootStartDate ?? null)
          : (row.startDate ?? null),
        endDate: hasExplicitRootDates
          ? (row.scheduleRootEndDate ?? null)
          : (row.endDate ?? null),
        widgetConfig: row.widgetConfig ?? null,
        playlists: [],
        ...(row.embeddedPlaylistFrames != null ? { embeddedPlaylistFrames: row.embeddedPlaylistFrames } : {}),
        ...(row.widgetAssetMetadata != null ? { widgetAssetMetadata: row.widgetAssetMetadata } : {}),
        ...(row.hlsVideoAssetMetadata != null ? { hlsVideoAssetMetadata: row.hlsVideoAssetMetadata } : {})
      }
      byRoot.set(key, root)
    }
    root.playlists.push(assignmentFromRegistrationEntry(row))
  }

  return order.map(k => byRoot.get(k)!)
}

/** Registration row metadata beyond {@link IChannelPayloadPlaylistAssignment} (for tests / debugging). */
export function registrationPlaylistEntryMetaFields(
  row: IPlayerRegistrationPlaylistEntry
): Pick<IPlayerRegistrationPlaylistEntry, (typeof REGISTRATION_ROW_META_KEYS)[number]> {
  return {
    version: row.version,
    playbackMode: row.playbackMode,
    ...(row.scheduleRootUrn != null && row.scheduleRootUrn !== ''
      ? { scheduleRootUrn: row.scheduleRootUrn }
      : {}),
    ...(row.scheduleRootStartDate !== undefined
      ? { scheduleRootStartDate: row.scheduleRootStartDate ?? null }
      : {}),
    ...(row.scheduleRootEndDate !== undefined
      ? { scheduleRootEndDate: row.scheduleRootEndDate ?? null }
      : {}),
    ...(row.embeddedPlaylistFrames != null
      ? { embeddedPlaylistFrames: row.embeddedPlaylistFrames as Record<string, TFrame[]> }
      : {}),
    ...(row.widgetAssetMetadata != null ? { widgetAssetMetadata: row.widgetAssetMetadata } : {}),
    ...(row.hlsVideoAssetMetadata != null ? { hlsVideoAssetMetadata: row.hlsVideoAssetMetadata } : {})
  }
}
